import { X, Loader2, Undo2, CreditCard, ListChecks } from 'lucide-react'

export interface RefundOptionsDialogProps {
  orderNumber: string
  /** Pre-formatted amount the full refund credits, e.g. "$5.58". */
  amountLabel: string
  /** Last four of the card that paid online; null when the BE omits it. */
  cardLastFour: string | null
  fullPending: boolean
  itemsPending: boolean
  onClose: () => void
  /** Gateway credit of the whole order + cancel. The dialog IS the
   *  confirmation — the caller runs it without a second prompt. */
  onFullRefund: () => void
  /** Item-level copy-with-refund flow (card or cash at checkout). */
  onChooseItems: () => void
}

// Refund chooser for an untouched online-paid order: the one case with two
// refund paths. Every other order goes straight to the item-level flow, so
// the order modal footer carries a single "Refund" button either way.
export function RefundOptionsDialog({
  orderNumber,
  amountLabel,
  cardLastFour,
  fullPending,
  itemsPending,
  onClose,
  onFullRefund,
  onChooseItems,
}: RefundOptionsDialogProps) {
  const busy = fullPending || itemsPending
  const cardText = cardLastFour ? `the card ending ${cardLastFour}` : 'the original card'

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={busy ? undefined : onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <Undo2 size={20} className="text-orange-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Refund order #{orderNumber}</h2>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3">
          <button
            onClick={onFullRefund}
            disabled={busy}
            className="w-full flex items-start gap-3 p-4 text-left border border-gray-200 rounded-xl hover:border-orange-300 hover:bg-orange-50 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="w-9 h-9 shrink-0 bg-orange-100 rounded-lg flex items-center justify-center">
              {fullPending ? (
                <Loader2 size={18} className="animate-spin text-orange-600" />
              ) : (
                <CreditCard size={18} className="text-orange-600" />
              )}
            </div>
            <div>
              <div className="font-semibold text-gray-900">
                {fullPending ? 'Refunding…' : 'Full refund to card'}
              </div>
              <div className="text-sm text-gray-500">
                {amountLabel} back to {cardText}. The order is canceled.
              </div>
            </div>
          </button>

          <button
            onClick={onChooseItems}
            disabled={busy}
            className="w-full flex items-start gap-3 p-4 text-left border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="w-9 h-9 shrink-0 bg-gray-100 rounded-lg flex items-center justify-center">
              {itemsPending ? (
                <Loader2 size={18} className="animate-spin text-gray-600" />
              ) : (
                <ListChecks size={18} className="text-gray-600" />
              )}
            </div>
            <div>
              <div className="font-semibold text-gray-900">
                {itemsPending ? 'Starting…' : 'Choose items'}
              </div>
              <div className="text-sm text-gray-500">
                Pick what's coming back, then refund it to the card or as cash.
              </div>
            </div>
          </button>
        </div>

        <div className="flex px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 py-3 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-50"
          >
            Keep Order
          </button>
        </div>
      </div>
    </div>
  )
}
